// The BD's draft session as state, and everything that changes it (TX-US-0028).
//
// The hub opens on what /api/hub/today says: the draft there is, if any, and whether today is
// reported. No draft is created by looking - one is opened by the first thing the BD does, and
// every write answers with the session, which replaces the one held here.
//
// A recording is sent the moment it exists. It is written to the device first (unsent.js) and
// removed from it only when the API has confirmed it in storage, so a failed upload is a take the
// BD still has, offered again here and the next time the hub is opened.

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  confirmHubRecordingUpload,
  deleteHubAttachment,
  deleteHubRecording,
  discardHubSession,
  getHubToday,
  openHubSession,
  requestHubRecordingUpload,
  saveHubText,
  submitHubSession,
} from '../api.js';
import { putDocument } from '../documents/files.js';
import { localDate, localTimezone } from './localDay.js';
import { holdUnsent, releaseUnsent, takeOf, unsentFor } from './unsent.js';

export const SENDING = Object.freeze({ idle: 'idle', sending: 'sending', failed: 'failed' });

// The device store is a safety net, not a dependency: a browser without it (a private window)
// still records and uploads, it only cannot offer the take again after a failure.
const quietly = (work) => work.catch(() => null);

export default function useHubSession(email) {
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState(null);
  const [draft, setDraftState] = useState(null);
  const [submittedToday, setSubmittedToday] = useState(false);
  const [unsent, setUnsent] = useState(null);
  const [sending, setSending] = useState(SENDING.idle);
  // The draft as an async action reads it, without waiting for a render.
  const current = useRef(null);

  const setDraft = useCallback((session) => {
    current.current = session;
    setDraftState(session);
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getHubToday(localDate()), quietly(unsentFor(email))])
      .then(([today, kept]) => {
        if (cancelled) return;
        setDraft(today.draft);
        setSubmittedToday(today.submitted_today);
        setUnsent(kept);
        setPhase('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setPhase('error');
      });
    return () => {
      cancelled = true;
    };
  }, [email, setDraft]);

  // The draft there is, or a new one dated in the BD's own day.
  const ensureDraft = useCallback(async () => {
    if (current.current) return current.current;
    const opened = await openHubSession(localDate(), localTimezone());
    setDraft(opened);
    return opened;
  }, [setDraft]);

  // Runs one change and reports its refusal beside the work, where the BD is looking.
  const attempt = useCallback(async (change) => {
    setError(null);
    try {
      return await change();
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, []);

  const sendRecording = useCallback(async (take) => {
    setSending(SENDING.sending);
    setError(null);
    await quietly(holdUnsent(email, take));
    try {
      const session = await ensureDraft();
      const contract = await requestHubRecordingUpload(session.session_id, take);
      await putDocument(
        take.blob, contract.upload_url, contract.required_headers, contract.upload_method,
      );
      setDraft(await confirmHubRecordingUpload(session.session_id));
      await quietly(releaseUnsent(email));
      setUnsent(null);
      setSending(SENDING.idle);
    } catch (err) {
      setError(err.message);
      setUnsent(await quietly(unsentFor(email)));
      setSending(SENDING.failed);
    }
  }, [email, ensureDraft, setDraft]);

  const sendUnsent = useCallback(
    () => (unsent ? sendRecording(takeOf(unsent)) : null),
    [unsent, sendRecording],
  );

  const dropUnsent = useCallback(async () => {
    await quietly(releaseUnsent(email));
    setUnsent(null);
    setSending(SENDING.idle);
  }, [email]);

  const removeRecording = useCallback(() => attempt(async () => {
    setDraft(await deleteHubRecording(current.current.session_id));
  }), [attempt, setDraft]);

  const removeAttachment = useCallback((attachmentId) => attempt(async () => {
    setDraft(await deleteHubAttachment(current.current.session_id, attachmentId));
  }), [attempt, setDraft]);

  // Nothing is opened to hold an empty note; a draft that exists has its text kept in step.
  const saveText = useCallback((text) => attempt(async () => {
    if (!current.current && !text.trim()) return;
    const session = await ensureDraft();
    if (session.text !== text) setDraft(await saveHubText(session.session_id, text));
  }), [attempt, ensureDraft, setDraft]);

  // Returns the submitted session, or null when it was refused.
  const submit = useCallback((text) => attempt(async () => {
    const session = await ensureDraft();
    if (session.text !== text) await saveHubText(session.session_id, text);
    const submitted = await submitHubSession(session.session_id);
    setDraft(null);
    if (submitted.local_date === localDate()) setSubmittedToday(true);
    return submitted;
  }), [attempt, ensureDraft, setDraft]);

  const discard = useCallback(() => attempt(async () => {
    if (current.current) await discardHubSession(current.current.session_id);
    setDraft(null);
    return true;
  }), [attempt, setDraft]);

  return {
    phase,
    error,
    setError,
    draft,
    setDraft,
    submittedToday,
    unsent,
    sending,
    ensureDraft,
    sendRecording,
    sendUnsent,
    dropUnsent,
    removeRecording,
    removeAttachment,
    saveText,
    submit,
    discard,
  };
}
